import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";
import { Card } from "../ui/CCard";
import { Sparkles, AlertTriangle, Loader2 } from "lucide-react";

// Per-session AI billing (training BE app/billing/ai_billing.py). Shown only
// for orgs a platform admin has switched to per-session AI - monthly orgs
// keep the AI add-on shown elsewhere on this page. Each AI session's fee is
// taken from the next payout; "dues" are fees no payout can cover (e.g. AI
// used on a Pay at Clinic appointment), payable here at any time.
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function AiBillingCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.fetchAiBilling()
      .then((res) => setData(res.data))
      .catch(() => setData(null));

  useEffect(() => {
    load();
  }, []);

  if (!data || data.mode !== "per_session") return null;

  const payDues = async () => {
    setBusy(true);
    try {
      const { loadRazorpay } = await import("../../utils/loadRazorpay");
      const orderRes = await api.payAiDues();
      const { payment_id, gateway_order_id, amount, key_id } = orderRes.data;
      const ready = await loadRazorpay();
      if (!ready || !key_id) {
        toast.error("Payment gateway is not available right now.");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: key_id,
        order_id: gateway_order_id,
        amount: Math.round(amount * 100),
        currency: "INR",
        name: "AI Session Charges",
        handler: async (response) => {
          try {
            await api.verifySubscriptionPayment({
              payment_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("AI dues paid. Thank you!");
            load();
          } catch {
            toast.error("Payment succeeded but could not be verified. Please contact support.");
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not start the payment.");
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="font-bold text-secondary-900">AI Sessions (pay per session)</h3>
            <p className="text-sm text-secondary-500">
              {inr(data.price_per_session)} per AI session, taken from your payouts.
            </p>
          </div>
        </div>
        {data.dues_amount >= 1 && (
          <button
            type="button"
            onClick={payDues}
            disabled={busy}
            className="shrink-0 inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Pay AI dues {inr(data.dues_amount)}
          </button>
        )}
      </div>

      {data.ai_paused && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">
            <strong>AI features are paused</strong> - {data.dues_count} unpaid AI session{data.dues_count === 1 ? "" : "s"} reached
            your limit of {data.max_unpaid_sessions}. Pay your AI dues to turn them back on. Everything else keeps working.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-secondary-50 p-4">
          <p className="text-xs text-secondary-500">Waiting for next payout</p>
          <p className="text-lg font-bold text-secondary-900">{inr(data.pending_amount - data.dues_amount)}</p>
          <p className="text-xs text-secondary-500">{data.pending_count - data.dues_count} session(s), deducted automatically</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-4">
          <p className="text-xs text-amber-700">AI dues to pay</p>
          <p className="text-lg font-bold text-amber-900">{inr(data.dues_amount)}</p>
          <p className="text-xs text-amber-700">{data.dues_count} session(s) with no online payment to deduct from</p>
        </div>
        <div className="rounded-xl bg-secondary-50 p-4">
          <p className="text-xs text-secondary-500">AI pauses at</p>
          <p className="text-lg font-bold text-secondary-900">{data.max_unpaid_sessions} unpaid sessions</p>
          <p className="text-xs text-secondary-500">Currently {data.dues_count}</p>
        </div>
      </div>

      {data.recent_charges?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-secondary-500 border-b border-secondary-100">
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Session</th>
                <th className="py-2 pr-4 font-medium">Amount</th>
                <th className="py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_charges.map((c) => (
                <tr key={c.id} className="border-b border-secondary-50">
                  <td className="py-2 pr-4">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN") : "-"}</td>
                  <td className="py-2 pr-4">{c.description || `Consultation ${c.consultation_id || ""}`}</td>
                  <td className="py-2 pr-4">{inr(c.amount)}</td>
                  <td className="py-2">
                    {c.status === "settled"
                      ? (c.settled_via === "payout" ? "Deducted from payout" : "Paid")
                      : c.status === "waived" ? "Waived" : "Unpaid"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
